/**
 * 자동 알림 — 채널×언어 템플릿 바인딩 매트릭스 (ID-226 명시 지정).
 *
 * dispatch 가 (channel, languageCode) 별 templateId/enabled 를 그대로 사용해 발송한다(be-notif 계약).
 * 모달은 활성 채널마다 4언어(KO_KR/EN_US/JA_JP/ZH_CN = 한국/영문/일본/중문몰) 행을 보여주고,
 * 각 셀에서 [발송 on/off] + [템플릿 지정(templateId|null=자동 폴백)] 을 편집한다.
 *
 * 이 모듈은 rule ↔ 매트릭스 ↔ 저장 payload 변환의 순수 로직(테스트 용이)을 담는다.
 */
import type { NotificationChannel } from '../types/notificationMessage';
import type { TemplateLanguageCode } from '../types/notificationTemplate';
import type { NotificationRule, TemplateBindingPayload } from '../types/notificationRule';
import { TEMPLATE_LANGUAGES } from '../types/notificationTemplate';

/** 매트릭스가 다루는 채널(이메일 우선·알림톡·SMS). KAKAO(소셜)는 자동알림 대상 아님. */
export const MATRIX_CHANNELS: ReadonlyArray<NotificationChannel> = ['EMAIL', 'KAKAO_BIZ', 'SMS'];

/** 한 셀(채널×언어) 상태. templateId=관리자 지정(null=자동 폴백), enabled=언어별 발송 on/off. */
export interface MatrixCell {
  templateId: string | null;
  enabled: boolean;
}

/** channel → (languageCode → cell). 활성 채널만 채운다. */
export type BindingMatrix = Record<string, Partial<Record<TemplateLanguageCode, MatrixCell>>>;

/** 기본 셀 — 미지정(자동 폴백) + 발송 on. */
const defaultCell = (): MatrixCell => ({ templateId: null, enabled: true });

/**
 * rule.templateBindings → 매트릭스.
 *  - languageCode 가 있는 바인딩: 해당 (channel, language) 셀에 그대로.
 *  - languageCode 가 null 인 구형 바인딩: 그 채널의 **전 언어**로 동일 templateId/enabled 펼침(back-compat).
 *    (단, 동일 채널에 언어별 바인딩이 함께 오면 언어별 값이 우선.)
 * 활성 채널(rule.channels)만 4언어 셀을 보장(없으면 기본 셀).
 */
export const ruleToMatrix = (rule: NotificationRule): BindingMatrix => {
  const matrix: BindingMatrix = {};
  const ensureChannel = (channel: NotificationChannel) => {
    if (!matrix[channel]) matrix[channel] = {};
    return matrix[channel];
  };

  // 1) 구형(언어 없음) 바인딩 먼저 전 언어로 펼침(언어별 값이 덮어쓰도록 선적용).
  for (const b of rule.templateBindings) {
    if (b.languageCode !== null) continue;
    const ch = ensureChannel(b.channel);
    for (const lang of TEMPLATE_LANGUAGES) {
      ch[lang] = { templateId: b.templateId, enabled: b.enabled };
    }
  }
  // 2) 언어별 바인딩 적용(구형 펼침을 덮어씀).
  for (const b of rule.templateBindings) {
    if (b.languageCode === null) continue;
    const ch = ensureChannel(b.channel);
    ch[b.languageCode] = { templateId: b.templateId, enabled: b.enabled };
  }

  // 3) 활성 채널은 4언어 셀 보장(미존재 언어는 기본 셀).
  for (const channel of rule.channels) {
    const ch = ensureChannel(channel);
    for (const lang of TEMPLATE_LANGUAGES) {
      if (!ch[lang]) ch[lang] = defaultCell();
    }
  }
  return matrix;
};

/** 채널 활성 토글 시 그 채널 셀(4언어)을 보장(기존 값 보존). */
export const ensureChannelCells = (matrix: BindingMatrix, channel: NotificationChannel): BindingMatrix => {
  const ch = { ...(matrix[channel] ?? {}) };
  for (const lang of TEMPLATE_LANGUAGES) {
    if (!ch[lang]) ch[lang] = defaultCell();
  }
  return { ...matrix, [channel]: ch };
};

/** 한 셀 패치(불변 갱신). */
export const setCell = (
  matrix: BindingMatrix,
  channel: NotificationChannel,
  lang: TemplateLanguageCode,
  patch: Partial<MatrixCell>,
): BindingMatrix => {
  const ch = { ...(matrix[channel] ?? {}) };
  const prev = ch[lang] ?? defaultCell();
  ch[lang] = { ...prev, ...patch };
  return { ...matrix, [channel]: ch };
};

/**
 * 매트릭스 → 저장 payload. **활성 채널**(activeChannels)의 4언어 셀만 평탄화.
 * 각 항목 = {channel, languageCode, templateId, enabled}. dispatch 가 그대로 사용.
 */
export const matrixToBindings = (
  matrix: BindingMatrix,
  activeChannels: ReadonlyArray<NotificationChannel>,
): TemplateBindingPayload[] => {
  const out: TemplateBindingPayload[] = [];
  for (const channel of MATRIX_CHANNELS) {
    if (!activeChannels.includes(channel)) continue;
    const ch = matrix[channel] ?? {};
    for (const lang of TEMPLATE_LANGUAGES) {
      const cell = ch[lang] ?? defaultCell();
      out.push({
        channel,
        languageCode: lang,
        templateId: cell.templateId,
        enabled: cell.enabled,
      });
    }
  }
  return out;
};
